import { create } from 'zustand';

import { SPEED_NETWORK_ID } from '@/lib/api/speed';

import type { Coin, NetworkId, PairSymbol } from './types';
import { coinToPair, DEFAULT_COIN, pairToCoin } from './types';

const TOKEN_PAIR_STORAGE_KEY = 'inout.market.selection.v2';

interface StoredMarketState {
    selectedCoin: Coin;
    networkId: NetworkId;
}

function isStoredMarketState(value: unknown): value is StoredMarketState {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const maybe = value as Record<string, unknown>;
    return (
        typeof maybe.selectedCoin === 'string' &&
        typeof maybe.networkId === 'number'
    );
}

function loadStoredMarketState(): StoredMarketState | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const raw = window.localStorage.getItem(TOKEN_PAIR_STORAGE_KEY);
    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as unknown;
        return isStoredMarketState(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function saveStoredMarketState(state: StoredMarketState): void {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(
            TOKEN_PAIR_STORAGE_KEY,
            JSON.stringify(state),
        );
    } catch {
        // ignore storage errors (private mode, quota, disabled storage)
    }
}

interface MarketSelectionState {
    /** Canonical Hyperliquid coin, e.g. "BTC". */
    selectedCoin: Coin;
    /** Price-stream symbol derived from the coin, e.g. "btc-usd". */
    selectedPair: PairSymbol;
    /** Always the speed-trading network (999). */
    networkId: NetworkId;
    setSelectedCoin: (coin: Coin) => void;
    /** Accepts a `coin-usd` symbol (from the market selector) or a bare coin. */
    setSelectedPair: (pair: PairSymbol) => void;
    setNetworkId: (networkId: NetworkId) => void;
}

const storedState = loadStoredMarketState();
const initialCoin = storedState?.selectedCoin ?? DEFAULT_COIN;
const initialNetworkId = storedState?.networkId ?? SPEED_NETWORK_ID;

export const useTokenPairStore = create<MarketSelectionState>((set, get) => ({
    selectedCoin: initialCoin,
    selectedPair: coinToPair(initialCoin),
    networkId: initialNetworkId,
    setSelectedCoin: (coin) => {
        saveStoredMarketState({
            selectedCoin: coin,
            networkId: get().networkId,
        });
        set({ selectedCoin: coin, selectedPair: coinToPair(coin) });
    },
    setSelectedPair: (pair) => {
        const coin = pairToCoin(pair);
        saveStoredMarketState({
            selectedCoin: coin,
            networkId: get().networkId,
        });
        set({ selectedCoin: coin, selectedPair: coinToPair(coin) });
    },
    setNetworkId: (networkId) => {
        saveStoredMarketState({
            selectedCoin: get().selectedCoin,
            networkId,
        });
        set({ networkId });
    },
}));
